import React from 'react';
import { X, MoreVertical, Copy, Pencil, Trash2, Ban, ChevronLeft, ChevronRight, CheckCircle2, Clock, XCircle, Palette } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import { directTriggerBookingStatus } from '../../lib/automations/directTrigger';

// Debug flag to control logging - set to false to disable logs
const enableHeaderDebug = false;

const statuses = [
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface BookingHeaderProps {
  bookingNumber: number;
  title: string;
  status: string;
  onClose: () => void;
  onEdit?: () => void;
  onEditColor?: () => void;
  onDelete?: () => void;
  onCancel?: () => void;
  onConvertToBooking?: () => void;
  isBooked?: boolean;
  createdAt?: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  onCompletedAtChange?: (completedAt: string) => void;
  readOnly?: boolean;
  isMobile?: boolean;
  bookingId?: string;
  businessId?: string;
  canEdit?: boolean;
}

export default function BookingHeader({
  bookingNumber,
  title,
  status,
  onClose,
  onEdit,
  onEditColor,
  onDelete,
  onCancel,
  onConvertToBooking,
  isBooked,
  createdAt,
  completedAt,
  onStatusChange,
  onCompletedAtChange,
  readOnly = false,
  isMobile = false,
  bookingId,
  businessId,
  canEdit = true
}: BookingHeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isEditingTimestamp, setIsEditingTimestamp] = useState(false);
  const [editTimestamp, setEditTimestamp] = useState('');

  // Status navigation logic
  const currentStatusIndex = statuses.findIndex(s => s.value === status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-400';
      case 'scheduled':
        return 'text-blue-400';
      case 'in_progress':
        return 'text-yellow-400';
      case 'completed':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'cancelled':
        return <XCircle className="h-3 w-3 text-red-400" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const handleStatusUp = async () => {
    if (canStatusGoUp && onStatusChange && !readOnly && canEdit) {
      const newStatus = statuses[currentStatusIndex - 1].value;
      
      // Only certain statuses should trigger automations
      const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
      
      if (enableHeaderDebug) {
        console.log(`[BOOKING HEADER] 🔍 Status change: ${status} → ${newStatus}`);
        console.log(`[BOOKING HEADER] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
        console.log(`[BOOKING HEADER] 🔍 Booking ID available: ${!!bookingId}`);
        console.log(`[BOOKING HEADER] 🔍 Business ID available: ${!!businessId}`);
      }
      
      // First call the parent's onStatusChange to update the database
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableHeaderDebug) {
          console.log(`[BOOKING HEADER] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[BOOKING HEADER] 📋 Booking ID: ${bookingId}`);
          console.log(`[BOOKING HEADER] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableHeaderDebug) {
              console.log(`[BOOKING HEADER] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[BOOKING HEADER] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[BOOKING HEADER] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[BOOKING HEADER] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[BOOKING HEADER] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[BOOKING HEADER] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  const handleStatusDown = async () => {
    if (canStatusGoDown && onStatusChange && !readOnly && canEdit) {
      const newStatus = statuses[currentStatusIndex + 1].value;
      
      // Only certain statuses should trigger automations
      const shouldTriggerAutomation = newStatus === 'in_progress' || newStatus === 'completed';
      
      if (enableHeaderDebug) {
        console.log(`[BOOKING HEADER] 🔍 Status change: ${status} → ${newStatus}`);
        console.log(`[BOOKING HEADER] 🔍 Should trigger automation: ${shouldTriggerAutomation}`);
        console.log(`[BOOKING HEADER] 🔍 Booking ID available: ${!!bookingId}`);
        console.log(`[BOOKING HEADER] 🔍 Business ID available: ${!!businessId}`);
      }
      
      // First call the parent's onStatusChange to update the database
      onStatusChange(newStatus);
      
      // If it's a status that should trigger automations and we have the booking/business ID
      if (shouldTriggerAutomation && bookingId && businessId) {
        if (enableHeaderDebug) {
          console.log(`[BOOKING HEADER] 🚀 Directly triggering automation for status: ${newStatus}`);
          console.log(`[BOOKING HEADER] 📋 Booking ID: ${bookingId}`);
          console.log(`[BOOKING HEADER] 📋 Business ID: ${businessId}`);
        }
        
        try {
          const result = await directTriggerBookingStatus(bookingId, newStatus, businessId);
          
          if (result.success) {
            if (enableHeaderDebug) {
              console.log(`[BOOKING HEADER] ✅ Successfully triggered ${result.results?.length || 0} actions`);
              // Log each action result for debugging
              if (result.results && result.results.length > 0) {
                result.results.forEach((actionResult, index) => {
                  console.log(`[BOOKING HEADER] 📋 Action ${index + 1}: Type=${actionResult.type}, Success=${actionResult.success}`);
                  if (!actionResult.success && actionResult.error) {
                    console.error(`[BOOKING HEADER] ❌ Action error:`, actionResult.error);
                  }
                });
              } else {
                console.log(`[BOOKING HEADER] ⚠️ No actions were triggered. Check if automations are configured correctly.`);
              }
            }
          } else {
            console.error(`[BOOKING HEADER] ❌ Failed to trigger automations:`, result.error);
          }
        } catch (error) {
          console.error(`[BOOKING HEADER] ❌ Error triggering automation:`, error);
        }
      }
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format timestamp for display
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Format timestamp for datetime-local input
  const formatTimestampForInput = (timestamp: string) => {
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Handle starting timestamp edit
  const handleEditTimestamp = () => {
    if (completedAt && status === 'completed' && !readOnly && canEdit) {
      setEditTimestamp(formatTimestampForInput(completedAt));
      setIsEditingTimestamp(true);
    }
  };

  // Handle saving timestamp changes
  const handleSaveTimestamp = () => {
    if (editTimestamp && onCompletedAtChange) {
      const newCompletedAt = new Date(editTimestamp).toISOString();
      onCompletedAtChange(newCompletedAt);
      setIsEditingTimestamp(false);
      setEditTimestamp('');
    }
  };

  // Handle canceling timestamp edit
  const handleCancelTimestamp = () => {
    setIsEditingTimestamp(false);
    setEditTimestamp('');
  };

  return (
    <>
      <div className="bg-[#1a1a1a] px-4 py-3 border-b border-[#2f2f2f]"
        style={{ 
          zIndex: 10,
          width: '100%'
        }}>
        
        {/* Top row with job number, title, and menu */}
        <div className="flex items-center justify-between">
          {/* Left side - Close button */}
          <div className="flex items-center">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-300">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Center - Booking number, title, and status */}
          <div className="flex-1 flex justify-center">
            <div className="text-center">
              <h2 className="text-lg font-semibold text-white">
                #{bookingNumber} - {title || 'Untitled Booking'}
              </h2>
              {status === 'canceled' && (
                <span className="text-sm text-red-400">Canceled</span>
              )}
              
              {/* Mobile Status Controller - underneath booking number and title */}
              {isMobile && onStatusChange && (
                <div className="mt-1.5 flex justify-center">
                  {status === 'cancelled' ? (
                    <div className="flex items-center space-x-1">
                      <XCircle className="h-3 w-3 text-red-400" />
                      <span className="text-red-400 text-xs font-medium">Cancelled</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      {/* Status Left Arrow */}
                      <button
                        onClick={handleStatusUp}
                        disabled={readOnly || !canStatusGoUp || !canEdit}
                        className={`p-0.5 rounded transition-colors ${
                          canStatusGoUp && !readOnly && canEdit
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </button>

                      {/* Current Status */}
                      <div className="flex flex-col items-center space-y-0.5">
                        <div className={`flex items-center space-x-1 ${getStatusColor(status)}`}>
                          {getStatusIcon(status)}
                          <span className="text-xs font-medium whitespace-nowrap">
                            {statuses[currentStatusIndex]?.label}
                          </span>
                        </div>
                        
                        {/* Status Progress Dots */}
                        <div className="flex space-x-0.5">
                          {statuses.map((_, index) => (
                            <div
                              key={index}
                              className={`h-0.5 w-2 rounded-full transition-colors ${
                                index <= currentStatusIndex ? 'bg-white' : 'bg-gray-600'
                              }`}
                            />
                          ))}
                        </div>
                      </div>

                      {/* Status Right Arrow */}
                      <button
                        onClick={handleStatusDown}
                        disabled={readOnly || !canStatusGoDown || !canEdit}
                        className={`p-0.5 rounded transition-colors ${
                          canStatusGoDown && !readOnly && canEdit
                            ? 'text-gray-400 hover:text-white' 
                            : 'text-gray-600 cursor-not-allowed'
                        }`}
                      >
                        <ChevronRight className="h-3 w-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Mobile Timestamp - underneath status controller */}
              {isMobile && (createdAt || completedAt) && (
                <div className="mt-2">
                  {isEditingTimestamp ? (
                    <div className="flex flex-col items-center space-y-2">
                      <input
                        type="datetime-local"
                        value={editTimestamp}
                        onChange={(e) => setEditTimestamp(e.target.value)}
                        className="text-xs px-2 py-1 rounded border border-gray-300 bg-white text-gray-900"
                      />
                      <div className="flex space-x-2">
                        <button
                          onClick={handleSaveTimestamp}
                          className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelTimestamp}
                          className="px-2 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400">
                      {status === 'completed' && completedAt ? (
                        <span 
                          onClick={handleEditTimestamp}
                          className={`${!readOnly && canEdit ? 'cursor-pointer hover:text-blue-400 hover:underline' : ''}`}
                          title={!readOnly && canEdit ? 'Click to edit completion time' : ''}
                        >
                          Completed at {formatTimestamp(completedAt)}
                        </span>
                      ) : createdAt ? (
                        <>Created at {formatTimestamp(createdAt)}</>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side - Menu and actions */}
          <div className="flex items-center space-x-3">
            {onConvertToBooking && !isBooked && (
              <button
                onClick={onConvertToBooking}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700"
              >
                Convert to Booking
              </button>
            )}
            {isBooked && (
              <span className="px-3 py-1.5 text-xs font-medium text-green-600 bg-green-100 rounded-md">
                Converted to Booking
              </span>
            )}
            {canEdit && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsMenuOpen(!isMenuOpen);
                  }}
                  className="p-1 text-gray-400 hover:text-gray-300 rounded-md"
                >
                  <MoreVertical className="h-5 w-5" />
                </button>

                {isMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1" style={{ zIndex: 9999999 }}>
                    {onEdit && status !== 'cancelled' && (
                      <button
                        onClick={() => {
                          onEdit();
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Pencil className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                    )}
                    {onEditColor && status !== 'cancelled' && (
                      <button
                        onClick={() => {
                          onEditColor();
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <Palette className="h-4 w-4 mr-2" />
                        Edit Color
                      </button>
                    )}
                    {onCancel && (
                      <button
                        onClick={() => {
                          setIsCancelModalOpen(true);
                          setIsMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2 text-sm flex items-center ${
                          status === 'cancelled' 
                            ? 'text-blue-600 hover:bg-blue-50' 
                            : 'text-red-600 hover:bg-red-50'
                        }`}
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        {status === 'cancelled' ? 'Schedule' : 'Cancel Booking'}
                      </button>
                    )}
                    {onDelete && (
                      <button
                        onClick={() => {
                          setIsDeleteModalOpen(true);
                          setIsMenuOpen(false);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setError(null);
        }}
        onConfirm={async () => {
          try {
            if (!onDelete) {
              throw new Error('Delete function not available');
            }
            setDeleteLoading(true);
            setError(null);
            await onDelete();
            setIsDeleteModalOpen(false);
          } catch (err) {
            console.error('Error deleting booking:', err);
            setError('Failed to delete booking');
          } finally {
            setDeleteLoading(false);
          }
        }}
        title="Delete Booking"
        message={`Are you sure you want to delete booking #${bookingNumber}? This action cannot be undone.`}
        loading={deleteLoading}
        error={error}
      />

      <DeleteConfirmationModal
        isOpen={isCancelModalOpen}
        onClose={() => {
          setIsCancelModalOpen(false);
          setError(null);
        }}
        onConfirm={async () => {
          try {
            if (!onCancel) {
              throw new Error('Cancel function not available');
            }
            setCancelLoading(true);
            setError(null);
            await onCancel();
            setIsCancelModalOpen(false);
          } catch (err) {
            console.error('Error updating booking:', err);
            setError('Failed to update booking');
          } finally {
            setCancelLoading(false);
          }
        }}
        title={status === 'cancelled' ? 'Schedule Booking' : 'Cancel Booking'}
        message={
          status === 'cancelled'
            ? `Are you sure you want to schedule booking #${bookingNumber}? This will make the booking active again.`
            : `Are you sure you want to cancel booking #${bookingNumber}? The booking will remain visible but marked as canceled.`
        }
        loading={cancelLoading}
        confirmText={status === 'cancelled' ? 'Schedule' : 'Cancel'}
        confirmButtonClass={
          status === 'cancelled' 
            ? 'bg-blue-600 hover:bg-blue-700' 
            : 'bg-red-600 hover:bg-red-700'
        }
        error={error}
      />
    </>
  );
}